/**
 * ワークスペースメンバー一覧 API hook
 */

import { RestApiError, restClient } from "lib/rest-client";
import { useCallback, useEffect, useState } from "react";

/**
 * ワークスペースメンバーの型
 */
export type WorkspaceMember = {
  userId: string;
  email: string;
  familyName: string;
  givenName: string;
  role: string;
};

type WorkspaceMemberListResponse = {
  members: WorkspaceMember[];
};

/**
 * ワークスペースメンバー一覧を取得する hook
 * @return {object} members, loading, error, fetchMembers
 */
export function useWorkspaceMembers(): {
  members: WorkspaceMember[];
  loading: boolean;
  error: RestApiError | null;
  fetchMembers: () => Promise<void>;
} {
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<RestApiError | null>(null);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response =
        await restClient.get<WorkspaceMemberListResponse>("/workspace/members");
      setMembers(response.members);
    } catch (e) {
      if (e instanceof RestApiError) {
        setError(e);
      } else {
        setError(
          new RestApiError(
            e instanceof Error ? e.message : "Unknown error",
            500,
          ),
        );
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  return { members, loading, error, fetchMembers };
}
